import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Column } from "primereact/column";
import { DataTable, DataTableStateEvent, SortOrder } from "primereact/datatable";
import { PencilIcon } from "@heroicons/react/24/outline";
import { useAppSelector } from "../../../app/hooks";
import { isHigherThanDistrictAdmin, UserRole } from "../../../app/userSlice";
import { ClassLinkOrCleverIconWithTooltip } from "../../../components/ClassLinkOrCleverIcon";
import SearchFilter from "../../../components/input/SearchFilter";
import SimplePagination from "../../../components/SimplePagination";
import { CustomSortIcon } from "../../../components/DatatableHelpers";
import { UserDistrictViewModel } from "../usersApiSlice";
import AddDistrictToUserButton from "./AddDistrictToUserButton";
import RemoveUserFromDistrict from "./RemoveUserFromDistrict";

interface UserDistrictTableModel extends UserDistrictViewModel {
	isRowSelected: boolean;
}

interface UserDistrictsProps {
	districts: UserDistrictViewModel[];
	userId: number;
	userName: string;
	canAddOrRemoveDistrict: boolean;
	editorsRole: UserRole;
}

export default function UserDistrictsSection(props: UserDistrictsProps) {
	const _displayLength = 3;
	const { config } = useAppSelector((state) => state.site);
	const hasClassLinkOrCleverDistrict = props.districts?.some(
		(s) => s.isClassLinkDistrict || s.isCleverDistrict
	);

	const [page, setPage] = useState(0);
	const [searchFilter, setSearchFilter] = useState("");
	const [isFiltered, setIsFiltered] = useState(false);
	const [sortField, setSortField] = useState<string>();
	const [sortOrder, setSortOrder] = useState<SortOrder>(0);
	const [selectedDistricts, setSelectedDistricts] = useState<UserDistrictViewModel[]>([]);
	const [isRemovingDistrict, setIsRemovingDistrict] = useState(false);

	const navigate = useNavigate();
	const location = useLocation();

	function resetTable() {
		setSelectedDistricts([]);
		setIsRemovingDistrict(false);
		setPage(0);
	}

	const filteredDistricts = useMemo(() => {
		return (
			props.districts.filter((district) => {
				if (!searchFilter) return true;
				const searchLower = searchFilter.toLowerCase();
				return (
					district.name.toLowerCase().includes(searchLower) ||
					district.externalID?.toLowerCase().includes(searchLower) ||
					district.code.toLowerCase().includes(searchLower)
				);
			}) ?? []
		);
	}, [props.districts, searchFilter]);

	const sortedDistricts = useMemo(() => {
		if (!sortField || sortOrder === 0 || !sortOrder) {
			return filteredDistricts;
		}

		const sorted = [...filteredDistricts].sort((a, b) => {
			const normalize = (val: string | null | undefined) => val ?? "";

			// Special handling for Location sorting: sort by state first, then by city as tiebreaker
			if (sortField === "location") {
				const aState = normalize(a.state);
				const bState = normalize(b.state);
				if (aState !== bState) {
					return aState.localeCompare(bState) * sortOrder;
				}
				const aCity = normalize(a.city);
				const bCity = normalize(b.city);
				return aCity.localeCompare(bCity) * sortOrder;
			}

			let aValue = normalize(a[sortField as keyof UserDistrictViewModel] as string);
			let bValue = normalize(b[sortField as keyof UserDistrictViewModel] as string);

			// Push empty strings to the end
			if (aValue === "" && bValue === "") return 0;
			if (aValue === "") return 1;
			if (bValue === "") return -1;

			return (
				aValue.localeCompare(bValue, undefined, {
					numeric: true, // Allow numbers to be sorted in a numerical order
					sensitivity: "base",
				}) * sortOrder
			);
		});

		return sorted;
	}, [filteredDistricts, sortField, sortOrder]);

	const onSort = (event: DataTableStateEvent) => {
		setSortField(event.sortField);
		setSortOrder(event.sortOrder ?? 0);
		setPage(0);
	};

	const paginatedDistricts = sortedDistricts.slice(
		page * _displayLength,
		(page + 1) * _displayLength
	);

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	useEffect(() => {
		setIsFiltered(!!searchFilter);
		setPage(0);
	}, [searchFilter]);

	const checkboxBodyTemplate = (row: UserDistrictTableModel) => {
		return (
			<input
				type="checkbox"
				value={row.districtID}
				checked={row.isRowSelected}
				onChange={handleSelectOne}
				className="cursor-pointer size-4 rounded-sm border-gray-300 text-primary-600 focus:ring-primary-600"
				aria-label={`Select district: ${row.name}`}
			/>
		);
	};

	const cleverOrClassLinkBodyTemplate = (row: UserDistrictTableModel) => {
		const tooltipContent = row.isCleverDistrict
			? "This district has a Clever account"
			: row.isClassLinkDistrict
				? "This district has a ClassLink account"
				: undefined;

		return (
			<ClassLinkOrCleverIconWithTooltip
				isClassLinkEntity={!!row.isClassLinkDistrict}
				isCleverEntity={!!row.isCleverDistrict}
				tooltipId={`district-${row.districtID}-outside-tooltip`}
				tooltipContent={tooltipContent}
			/>
		);
	};

	const locationBodyTemplate = (row: UserDistrictTableModel) => {
		return (
			<span>
				<span>
					<span className="font-medium!">State:</span> {row.state ?? "-"}
				</span>
				<div className="mt-1 text-gray-500 text-sm">
					<span className="font-medium!">City:</span> {row.city ?? "-"}
				</div>
			</span>
		);
	};

	const editBodyTemplate = (row: UserDistrictTableModel) => {
		return (
			<button
				id={`edit-${row.districtID}-button`}
				className="mr-2 cursor-pointer"
				onClick={() => {
					const returnTo = location.pathname + location.search;
					navigate(`/districts/edit?districtId=${row.districtID}`, {
						state: {
							returnTo: returnTo,
						},
					});
				}}>
				<PencilIcon className="size-5" />
			</button>
		);
	};

	const handleSelectOne = (e: ChangeEvent<HTMLInputElement>) => {
		const districtId = e.currentTarget.value;
		const isChecked = e.currentTarget.checked;

		const district = props.districts.find((d) => String(d.districtID) === districtId);
		if (!district) return;

		if (isChecked) {
			setSelectedDistricts((prev) => [...prev, district]);
		} else {
			setSelectedDistricts((prev) =>
				prev.filter((d) => d.districtID !== district.districtID)
			);
		}
	};

	const handleSelectAll = (isChecked: boolean) => {
		if (isChecked) {
			// Add any missing districts from filteredDistricts to selectedDistricts
			setSelectedDistricts((prev) => {
				const newDistricts = filteredDistricts.filter(
					(fd) => !prev.some((sd) => sd.districtID === fd.districtID)
				);
				return [...prev, ...newDistricts];
			});
		} else {
			// Remove all filteredDistricts from selectedDistricts
			setSelectedDistricts((prev) =>
				prev.filter(
					(sd) => !filteredDistricts.some((fd) => fd.districtID === sd.districtID)
				)
			);
		}
	};

	const allChecked = useMemo(() => {
		return (
			filteredDistricts.length > 0 &&
			filteredDistricts.every((d) =>
				selectedDistricts.some((sd) => sd.districtID === d.districtID)
			)
		);
	}, [filteredDistricts, selectedDistricts]);

	const districtsWithSelection: UserDistrictTableModel[] = useMemo(() => {
		return paginatedDistricts.map((district) => ({
			...district,
			isRowSelected: selectedDistricts.some((d) => d.districtID === district.districtID),
		}));
	}, [paginatedDistricts, selectedDistricts]);

	return (
		<section>
			<hr className="my-6" />
			<h2>Districts</h2>
			<div className="flex gap-3 mt-4 justify-between items-end">
				<SearchFilter
					id="districtSearch"
					name="districtSearch"
					searchFilter={searchFilter}
					setSearchFilter={setSearchFilter}
					label="Search"
					placeHolder="Search by district name, code or external ID..."
					className="w-[400px]"
				/>
				{props.canAddOrRemoveDistrict && (
					<div className="flex gap-x-3 items-end">
						<AddDistrictToUserButton
							userId={props.userId}
							districtIds={props.districts.map((d) => d.districtID)}
						/>
						<RemoveUserFromDistrict
							selectedDistricts={selectedDistricts}
							isLoading={isRemovingDistrict}
							name={props.userName}
							userId={props.userId}
							resetTable={resetTable}
							setIsLoading={setIsRemovingDistrict}
						/>
					</div>
				)}
			</div>
			<DataTable
				value={districtsWithSelection}
				loading={isRemovingDistrict}
				dataKey="districtID"
				emptyMessage="No districts found"
				stripedRows
				className="tbl tbl-data tbl-datatable min-w-[40rem] cursor-default"
				style={{ marginTop: "16px" }}
				sortField={sortField}
				sortOrder={sortOrder}
				onSort={onSort}
				sortIcon={CustomSortIcon}>
				{props.canAddOrRemoveDistrict && (
					<Column
						columnKey="district-checkbox"
						header={
							<input
								type="checkbox"
								checked={allChecked}
								onChange={(e) => handleSelectAll(e.target.checked)}
								className="cursor-pointer size-4 rounded-sm border-gray-300 text-primary-600 focus:ring-primary-600"
								aria-label="Select all districts"
							/>
						}
						body={checkboxBodyTemplate}
						className="w-12"
						headerClassName="text-center"
						bodyClassName="text-center"
					/>
				)}
				{!config?.isUtahComposeSite && hasClassLinkOrCleverDistrict && (
					<Column
						body={cleverOrClassLinkBodyTemplate}
						headerClassName="!pr-0 w-4"
						className="!pr-0"></Column>
				)}
				<Column header="Name" field="name" sortable></Column>
				<Column header="District Code" field="code" sortable />
				<Column header="External ID" field="externalID" sortable></Column>
				<Column
					header="Location"
					body={locationBodyTemplate}
					sortable
					sortField="location"></Column>
				{isHigherThanDistrictAdmin(props.editorsRole) && (
					<Column body={editBodyTemplate} className="w-4 !pl-1 !pr-2"></Column>
				)}
			</DataTable>
			<SimplePagination
				itemCount={sortedDistricts?.length}
				page={page}
				pageSize={_displayLength}
				isFiltered={isFiltered}
				totalCount={props.districts?.length}
				onChange={(page) => handlePageChange(page)}
				className={"border-none"}
			/>
		</section>
	);
}
